import React, { useEffect, useRef, useState } from "react";
import "./product-card.css";
import { Link } from "react-router-dom";
import { Icon } from "@/components/ui-components/icon";
import { formatCurrency } from "@/utils/format";
import { isPlaceholderImage } from "@/utils/assets";
import { calculateProductTotal, type Product } from "@/data/products";
import type { LocaleCode } from "@/i18n/locales";
import type { InterpolationValues } from "@/i18n/types";
import { productDetailsPath } from "@/utils/product-url";

interface Props {
    product: Product;
    locale: LocaleCode;
    categoryLabel: string;
    categoryPath?: string;
    quantity: number;
    t: (key: string, values?: InterpolationValues) => string;
    onQuantityChange: (id: string, quantity: number) => void;
    onAdd: (id: string) => void;
}

export function ProductCard({ product, locale, categoryLabel, categoryPath, quantity, t, onQuantityChange, onAdd }: Props) {
    const [added, setAdded] = useState(false);
    const timerRef = useRef<number | null>(null);
    const badge = product.badge === "pitango" ? t("commerce.product.badgePitango") : product.badge === "popular" ? t("commerce.product.badgePopular") : product.badge?.toUpperCase();
    const pricing = calculateProductTotal(product, quantity);
    const unitPrice = product.price !== null ? formatCurrency(pricing.total / quantity, locale) : null;
    const originalPrice = product.price !== null && pricing.savings > 0 ? formatCurrency(product.price, locale) : null;

    useEffect(() => () => { if (timerRef.current !== null) window.clearTimeout(timerRef.current); }, []);

    const add = () => {
        onAdd(product.id);
        setAdded(true);
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setAdded(false), 950);
    };

    return (
        <article className="product" data-product={product.id}>
            <div className="product-media">
                {badge && <span className="badge">{badge}</span>}
                <Link className="product-media-link" to={productDetailsPath(locale, product)} aria-label={`${t("commerce.product.detailTitle")}: ${product.name}`}>
                    <img className={`product-photo${isPlaceholderImage(product.image) ? " product-photo--placeholder" : ""}`} src={product.image} alt={product.name} />
                </Link>
            </div>
            <div className="product-body">
                <div className="product-summary-row">
                    <div className="product-summary-copy">
                        <h3 dir="auto"><Link className="product-title-button" to={productDetailsPath(locale, product)}>{product.name}</Link></h3>
                        {unitPrice !== null ? (
                            <div className="price">
                                <div className="price-row"><span className="current-price" dir="ltr">{unitPrice}</span>{originalPrice !== null ? <del dir="ltr">{originalPrice}</del> : null}</div>
                                {pricing.promotion ? <span className="promotion-note">{pricing.promotion.discountType === "Fix" ? t("commerce.product.bundleOffer", { quantity: pricing.promotion.minimumBuyQuantity, price: formatCurrency(pricing.promotion.discountValue, locale) }) : t("commerce.product.percentOffer", { percent: pricing.promotion.discountValue })}</span> : null}
                                <span className="vat">{t("commerce.product.vatIncluded")}</span>
                            </div>
                        ) : null}
                        {categoryPath ? <Link className="product-category" to={categoryPath}>{categoryLabel}</Link> : <span className="product-category">{categoryLabel}</span>}
                    </div>
                </div>
                <div className="product-sub" dir="auto">{product.description}</div>
                {product.price !== null ? <div className="buy-row">
                    <div className="qty"><button type="button" aria-label={t("common.accessibility.decreaseQuantity")} onClick={() => onQuantityChange(product.id, Math.max(1, quantity - 1))}><Icon name="minus" size="sm" /></button><span>{quantity}</span><button type="button" aria-label={t("common.accessibility.increaseQuantity")} onClick={() => onQuantityChange(product.id, quantity + 1)}><Icon name="plus" size="sm" /></button></div>
                    <button className={`add-btn${added ? " added" : ""}`} type="button" onClick={add}><Icon name="cart" size="sm" /><span>{t(`commerce.product.${added ? "added" : "add"}`)}</span></button>
                </div> : null}
            </div>
        </article>
    );
}
